import React, { memo, useCallback, useState, useEffect, useRef } from 'react';
import { Button, Input, Select } from 'antd';
import { Tooltip, useMediaQuery } from '@mui/material';
import { SendOutlined, StopOutlined } from '@ant-design/icons';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../../firebaseConfig';
import VoiceInput from './VoiceInput';

/**
 * ChatInput component for sending messages
 */
const ChatInput = memo(({
    onSendMessage,
    disabled,
    selectedModel,
    onModelChange,
    aiModels,
    isChatLoading,
    value,
    onChange,
    selectedChatId,
    selectedInstrument,
    selectedCategory,
    inputRef,
    onDisabledInputClick,
    onStopGeneration,
    isStreaming = false
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showMobileModelSelect, setShowMobileModelSelect] = useState(false);
    const isMobile = useMediaQuery('(max-width:768px)');
    const mobileModelRef = useRef(null);
    const MAX_CHARS = 4000;

    // Handle voice transcript
    const handleVoiceTranscript = useCallback((transcript, isFinal) => {
        if (transcript.length <= MAX_CHARS) {
            onChange(transcript);
        }
    }, [onChange]);

    // Get current model label for display
    const currentModelLabel = aiModels.find(m => m.value === selectedModel)?.label || selectedModel;

    // Close mobile model dropdown when clicking outside
    useEffect(() => {
        if (!showMobileModelSelect) return;

        const handleClickOutside = (event) => {
            if (mobileModelRef.current && !mobileModelRef.current.contains(event.target)) {
                setShowMobileModelSelect(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showMobileModelSelect]);

    const handleSend = useCallback(() => {
        if (value.trim()) {
            // Log message sending attempt
            logEvent(analytics, 'chat_message_sent', {
                chat_id: selectedChatId,
                message_length: value.trim().length,
                instrument: selectedInstrument,
                category: selectedCategory
            });

            onSendMessage(value.trim());
            onChange("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, onSendMessage, onChange]);

    const handleKeyDown = useCallback((e) => {
        // Enter without Shift sends the message
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // Only send if not loading and not disabled (no chat selected)
            if (!isChatLoading && !disabled && value.trim()) {
                handleSend();
            }
        }
        // Escape stops generation
        if (e.key === 'Escape' && isStreaming && onStopGeneration) {
            e.preventDefault();
            onStopGeneration();
        }
        // Shift+Enter allows newline (default behavior, no need to handle)
    }, [handleSend, isChatLoading, disabled, value, isStreaming, onStopGeneration]);

    // Only disable text input when no chat is selected
    // Allow input even when waiting for response
    const isInputDisabled = !selectedChatId;

    const handleInputClick = useCallback(() => {
        if (isInputDisabled && onDisabledInputClick) {
            onDisabledInputClick();
        }
    }, [isInputDisabled, onDisabledInputClick]);

    const handleFocus = useCallback(() => setIsFocused(true), []);
    const handleBlur = useCallback(() => setIsFocused(false), []);

    const placeholder = isInputDisabled
        ? "Select an instrument above to start chatting with AI"
        : "Message NanoTrader AI...";

    const tooltipTitle = isInputDisabled
        ? "Choose a trading pair or stock from the selector above to begin"
        : "";

    const charCount = value.length;
    const isNearLimit = charCount > MAX_CHARS * 0.9;

    // Handle mobile model selection
    const handleMobileModelChange = useCallback((newModel) => {
        onModelChange(newModel);
        setShowMobileModelSelect(false);
    }, [onModelChange]);

    return (
        <div className="ntb-chat-input">
            {/* Mobile model selector chip */}
            {isMobile && !isInputDisabled && (
                <div className="ntb-mobile-model-row" ref={mobileModelRef}>
                    <button
                        className="ntb-mobile-model-chip"
                        onClick={() => setShowMobileModelSelect(!showMobileModelSelect)}
                        disabled={isChatLoading}
                        aria-expanded={showMobileModelSelect}
                        aria-haspopup="listbox"
                    >
                        <SmartToyOutlinedIcon className="ntb-mobile-model-icon" />
                        <span className="ntb-mobile-model-name">
                            {currentModelLabel.replace(' 🌟', '').replace('(D) ', '')}
                        </span>
                        <span className="ntb-mobile-model-arrow">▾</span>
                    </button>
                    {showMobileModelSelect && (
                        <div className="ntb-mobile-model-dropdown">
                            {aiModels.map(model => (
                                <button
                                    key={model.value}
                                    className={`ntb-mobile-model-option ${model.value === selectedModel ? 'active' : ''} ${model.disabled ? 'disabled' : ''}`}
                                    onClick={() => !model.disabled && handleMobileModelChange(model.value)}
                                    disabled={model.disabled}
                                >
                                    {model.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
            <div className="ntb-chat-input-container">
                <div
                    className={`ntb-chat-input-wrapper ${isInputDisabled ? 'ntb-chat-input-wrapper-disabled' : ''} ${isFocused ? 'focused' : ''}`}
                    onClick={handleInputClick}
                    style={isInputDisabled ? { cursor: 'not-allowed' } : {}}
                >
                    <div className="ntb-chat-input-main">
                        {!isInputDisabled && !isMobile && (
                            <div className="ntb-model-row">
                                <Select
                                    value={selectedModel}
                                    onChange={onModelChange}
                                    options={aiModels.map(model => ({
                                        ...model,
                                        label: model.label
                                    }))}
                                    disabled={isChatLoading}
                                    size="small"
                                    className="ntb-model-select-inline"
                                    variant="borderless"
                                    popupMatchSelectWidth={false}
                                />
                            </div>
                        )}
                        <Tooltip
                            title={tooltipTitle}
                            placement="top"
                            enterDelay={200}
                            arrow
                            disableHoverListener={!isInputDisabled}
                        >
                            <div className="ntb-chat-input-field">
                                <Input.TextArea
                                    ref={inputRef}
                                    value={value}
                                    onChange={(e) => {
                                        if (e.target.value.length <= MAX_CHARS) {
                                            onChange(e.target.value);
                                        }
                                    }}
                                    onKeyDown={handleKeyDown}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    placeholder={placeholder}
                                    disabled={isInputDisabled}
                                    autoSize={{ minRows: isFocused ? 2 : 1, maxRows: 6 }}
                                    showCount={false}
                                    className={isInputDisabled ? 'ntb-chat-input-disabled' : ''}
                                    aria-label="Chat message input"
                                    aria-describedby={isInputDisabled ? "input-disabled-help" : undefined}
                                />
                            </div>
                        </Tooltip>
                    </div>
                    {!isInputDisabled && value.length > 0 && (
                        <div className={`ntb-char-count ${isNearLimit ? 'near-limit' : ''}`}>
                            {charCount}/{MAX_CHARS}
                        </div>
                    )}
                    {!isInputDisabled && (
                        <div className="ntb-input-actions">
                            {!isStreaming && (
                                <VoiceInput
                                    onTranscript={handleVoiceTranscript}
                                    disabled={isChatLoading}
                                />
                            )}
                            {isStreaming ? (
                                <Tooltip title="Stop generating (Esc)" arrow placement="top">
                                    <span>
                                        <Button
                                            className="ntb-chat-stop-button"
                                            onClick={onStopGeneration}
                                            type="default"
                                            danger
                                            icon={<StopOutlined />}
                                            aria-label="Stop generating"
                                        />
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Enter to send, Shift+Enter for new line" arrow placement="top">
                                    <span>
                                        <Button
                                            className="ntb-chat-send-button"
                                            onClick={handleSend}
                                            type="primary"
                                            disabled={disabled || isChatLoading || !value.trim()}
                                            icon={<SendOutlined />}
                                            aria-label={isChatLoading ? "Waiting for response" : "Send message"}
                                        />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
                {!isInputDisabled && (
                    <div className="ntb-input-bottom-row">
                        <Select
                            value={selectedModel}
                            onChange={onModelChange}
                            options={aiModels.map(model => ({
                                ...model,
                                label: model.label
                            }))}
                            disabled={isChatLoading}
                            size="small"
                            className="ntb-model-select-bottom"
                            variant="borderless"
                            popupMatchSelectWidth={false}
                        />
                        <span className="ntb-input-hint">
                            <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for new line
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
});

export default ChatInput; 
